/**
 * University of Nicosia (UNIC, Cyprus) brand colors — S3 (2026-10-14).
 *
 * Sources (admin_tools/backend/data/onboarding/unic/probes/brand_census.json): no public brand
 * guide was found. Two first-party sources:
 *   1. The official logo (UNIC-Logo-400x132.png): red #BD081C (9,413 of 11,146 opaque non-white
 *      pixels) + cool grey #53565A (703 px).
 *   2. The site theme: Avada's global palette has exactly one chromatic colour,
 *      --awb-color6 #bb1d2c, a near-identical red; the rest are greys.
 * The logo is the brand artwork, so its red is primary (decisions P7; UNIC may supply a
 * guide that overrules this).
 *
 * Contrast (WCAG, measured — onboarding/unic/theme_measurements.json; 12 px text needs 4.5:1):
 *   Logo red #BD081C   on white 6.53, paper 5.80, sunken 5.31; on #161412 2.81, #1F1C19 2.60.
 *                      White text on it 6.53, so `-strong` is the base brand color.
 *   Logo grey #53565A  on white 7.38, paper 6.56, sunken 5.99; on #161412 2.49, #1F1C19 2.30.
 *   Accent token = logo grey at 100%. KNOWN LIMITATION: neither brand colour reaches the 3:1
 *   floor on the dark surfaces, and the theme system has no dark-mode accent token (doc 51
 *   S3-6). Light appearance is the supported appearance.
 *
 * Ladders are HSL interpolations around the brand values (theme_tools.py), pinned at red = 600
 * and grey = 700: brand-aligned for UI states, not an officially published palette.
 *
 * Brand mark (components/ui/BrandMark.tsx): the FRONT face is the logo red, the top face the logo
 * grey, the side a red shade — the logo's own two colours. (A first version used a grey front to
 * clear the cross-theme distinctness floor, which the red failed against SDSU's red; the owner
 * removed that floor on 2026-10-14 because it does not scale, so the mark follows the brand.)
 * Dark appearance: a 30% red tint front (legible on #161412) and a grey tint top. Text-only
 * branding, no logo asset embedded (mirrors the other themes).
 */
import type { ThemeColors } from '../types';

// The logo red at 100% — primary + mark shading.
const UNIC_RED = '189 8 28'; // #BD081C
// The logo grey at 100% — the accent token and the mark top face.
const UNIC_GREY = '83 86 90'; // #53565A

export const unicColors: ThemeColors = {
  // Primary — UNIC red (logo #BD081C)
  '--color-primary': UNIC_RED,
  // -strong: white text passes at 6.53:1, so -strong is the base brand color.
  '--color-primary-strong': UNIC_RED,
  '--color-primary-50': '254 235 237',
  '--color-primary-100': '253 216 220',
  '--color-primary-200': '252 177 185',
  '--color-primary-300': '250 128 141',
  '--color-primary-400': '247 74 93',
  '--color-primary-500': '245 20 45',
  '--color-primary-600': UNIC_RED,
  '--color-primary-700': '157 7 23',
  '--color-primary-800': '117 5 17',
  '--color-primary-900': '83 4 12',

  // Accent — UNIC grey (logo #53565A) at 100%; see header for the dark-mode limitation.
  '--color-accent': UNIC_GREY,
  '--color-accent-50': '244 245 245',
  '--color-accent-100': '234 234 235',
  '--color-accent-200': '213 214 216',
  '--color-accent-300': '186 188 191',
  '--color-accent-400': '157 160 164',
  '--color-accent-500': '128 132 138',
  '--color-accent-600': '103 106 111',
  '--color-accent-700': UNIC_GREY,
  '--color-accent-800': '59 61 64',
  '--color-accent-900': '42 43 45',

  // Brand-mark faces — the logo's red front and grey top; red-shade side.
  '--color-mark-front': UNIC_RED,
  '--color-mark-top': UNIC_GREY,
  '--color-mark-side': '117 5 17',
  '--color-mark-front-dm': '209 82 96',
  '--color-mark-top-dm': '152 154 156',
  '--color-mark-side-dm': '157 7 23',
};
